import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";

import { ButtonType, HeroButton } from "@gaming/xds-react-js";

import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { LoadingSpinner } from "../../components/Spinner/Spinner";
import { PageType } from "../../constants";
import { DefaultTeamMemberData } from "../../models/ticketing";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getIsTicketingAdmin,
  getIsTicketingManager,
  getTicketingDefaultTeamMembers,
  getTicketingIsAddingMembers,
  getTicketingIsDefaultTeamMembersFetching,
  getTicketingIsRenamingTeam,
  getTicketingIsTeamDetailFetching,
  getTicketingSelectedAccessId,
  getTicketingTeamDetail,
  getTicketingTeamDetailError,
} from "../../redux/selectors/ticketingSelectors";
import { AccessDeniedPage } from "../AccessDeniedPage/AccessDeniedPage";
import { useTicketingStrings } from "../TicketingPage/ticketingStrings";
import { useRequireTicketingAccess } from "../TicketingPage/useRequireTicketingAccess";
import * as styles from "./TeamDetailPage.module.css";

/**
 * Traps focus within a modal container. Returns a ref to attach to the modal element.
 * Optionally calls onEscape when the Escape key is pressed.
 */
function useFocusTrap<T extends HTMLElement = HTMLDivElement>(onEscape?: () => void) {
  const ref = useRef<T>(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }

    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () =>
      Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        el => el.offsetParent !== null
      );

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onEscapeRef.current?.();
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    // Focus first focusable element on mount
    const focusable = getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, []);

  return ref;
}

type TeamDetailParams = {
  teamId: string;
};

const TeamDetailPage: React.FC = () => {
  const { isSignedIn, isVerifyChecked, isVerified } = useRequireTicketingAccess();
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const { teamId } = useParams<TeamDetailParams>();
  const accessId = useSelector(getTicketingSelectedAccessId);
  const teamDetail = useSelector(getTicketingTeamDetail);
  const isFetching = useSelector(getTicketingIsTeamDetailFetching);
  const error = useSelector(getTicketingTeamDetailError);
  const isRenaming = useSelector(getTicketingIsRenamingTeam);
  const isAdmin = useSelector(getIsTicketingAdmin);
  const isManager = useSelector(getIsTicketingManager);
  const canManageTeam = isAdmin || isManager;

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Read accessId from URL if not in Redux
  useEffect(() => {
    if (!accessId && isVerifyChecked) {
      const urlAccessId = new URLSearchParams(window.location.search).get("accessid");
      if (urlAccessId) {
        dispatch(ticketingActions.setAccessId(urlAccessId));
      }
    }
  }, [dispatch, accessId, isVerifyChecked]);

  // Fetch team detail when we have teamId + accessId
  useEffect(() => {
    if (teamId && accessId) {
      dispatch(ticketingActions.fetchTeamDetail.start(teamId, accessId));
    }
  }, [dispatch, teamId, accessId]);

  // Ensure role info is loaded (needed for canManageTeam)
  useEffect(() => {
    if (accessId && isAdmin === undefined) {
      dispatch(ticketingActions.fetchTeamsAdmin.start(accessId));
    }
  }, [dispatch, accessId, isAdmin]);

  // Auto-open Add Members modal when navigating from team creation
  const shouldOpenAddMembers = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("addMembers") === "true") {
      shouldOpenAddMembers.current = true;
      // Remove the param from URL immediately to prevent re-triggers on reload
      params.delete("addMembers");
      const newSearch = params.toString();
      const newUrl = history.location.pathname + (newSearch ? `?${newSearch}` : "");
      history.replace(newUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldOpenAddMembers.current && teamDetail && !teamDetail.isDefault) {
      shouldOpenAddMembers.current = false;
      setIsAddModalOpen(true);
    }
  }, [teamDetail]);

  // Close overflow menu on outside click or Escape
  useEffect(() => {
    if (!openMenuId) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  const settingsBackUrl = useMemo(() => {
    const resolvedAccessId =
      accessId ||
      (typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("accessid") || ""
        : "");
    return resolvedAccessId
      ? `${OnboardingUrls[RouteNames.TicketingSettings]}?accessid=${resolvedAccessId}`
      : OnboardingUrls[RouteNames.TicketingSettings];
  }, [accessId]);

  const handleRemoveMember = useCallback(
    (contactId: string) => {
      if (accessId && teamId) {
        dispatch(ticketingActions.removeTeamMember.start(teamId, contactId, accessId));
      }
    },
    [dispatch, teamId, accessId]
  );

  const handleToggleMemberStatus = useCallback(
    (contactId: string, activate: boolean) => {
      if (accessId && teamId) {
        dispatch(ticketingActions.toggleMemberStatus.start(teamId, contactId, activate, accessId));
      }
    },
    [dispatch, teamId, accessId]
  );

  const handleRemoveManager = useCallback(
    (contactId: string) => {
      if (accessId && teamId) {
        dispatch(ticketingActions.removeTeamManager.start(teamId, contactId, accessId));
      }
    },
    [dispatch, teamId, accessId]
  );

  const handleAssignManager = useCallback(
    (contactId: string) => {
      if (accessId && teamId) {
        dispatch(ticketingActions.assignTeamManager.start(teamId, [contactId], accessId));
      }
    },
    [dispatch, teamId, accessId]
  );

  if (!isSignedIn || !isVerifyChecked) {
    return (
      <>
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.spinnerContainer}>
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!isVerified) {
    return (
      <AccessDeniedPage
        footNote={strings.ticketAccessDeniedMsg}
        pageType={PageType.IdXboxApplication}
      />
    );
  }

  if (isFetching && (!teamDetail || teamDetail.teamId !== teamId)) {
    return (
      <>
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.spinnerContainer}>
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    history.replace(settingsBackUrl);
    return null;
  }

  if (!teamDetail) {
    return (
      <>
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.spinnerContainer}>
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.page}>
        <div className={styles.card}>
          <Link className={styles.backLink} to={settingsBackUrl}>
            {strings.ticketTeamDetailBackToSettings}
          </Link>

          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              <h1 className={styles.teamTitle}>
                {teamDetail.teamName}
                {teamDetail.isDefault && <span className={styles.defaultBadge}>Default</span>}
              </h1>

              <div className={styles.metaRow}>
                <span
                  className={cx(styles.statusBadge, {
                    [styles.statusActive]: teamDetail.isActive,
                    [styles.statusInactive]: !teamDetail.isActive,
                  })}
                >
                  {teamDetail.isActive ? "ACTIVE" : "INACTIVE"}
                </span>
                {canManageTeam && teamDetail.isActive && !teamDetail.isDefault && (
                  <button
                    className={styles.statusAction}
                    onClick={() =>
                      accessId &&
                      dispatch(
                        ticketingActions.toggleTeamStatus.start(teamDetail.teamId, false, accessId)
                      )
                    }
                  >
                    Inactivate
                  </button>
                )}
                <span>
                  {strings.ticketTeamDetailCreatedOn.replace("{0}", teamDetail.createdOn)}
                </span>
              </div>
            </div>

            <div className={styles.actions}>
              {canManageTeam && (
                <HeroButton
                  as="button"
                  buttonType={ButtonType.Secondary}
                  className={styles.renameButton}
                  onClick={() => setIsRenameModalOpen(true)}
                  disabled={isRenaming}
                >
                  {strings.ticketTeamDetailRename}
                </HeroButton>
              )}
            </div>
          </div>

          {canManageTeam && !teamDetail.isActive && !teamDetail.isDefault && (
            <div className={styles.inactiveBanner}>
              <span className={styles.inactiveBannerText}>
                {strings.ticketTeamDetailInactiveBanner.replace("{0}", teamDetail.teamName)}
              </span>
              <button
                className={styles.inactiveBannerActivate}
                onClick={() =>
                  accessId &&
                  dispatch(
                    ticketingActions.toggleTeamStatus.start(teamDetail.teamId, true, accessId)
                  )
                }
              >
                {strings.ticketTeamDetailInactiveBannerActivate}
              </button>
            </div>
          )}

          {/* Members Table */}
          <div className={styles.membersSection}>
            <div className={styles.membersHeader}>
              <h3 className={styles.membersTitle}>
                {strings.ticketTeamDetailMembersTitle} ({teamDetail.memberCount} active)
              </h3>
              {canManageTeam && !teamDetail.isDefault && (
                <HeroButton
                  as="button"
                  className={styles.addButton}
                  onClick={() => setIsAddModalOpen(true)}
                >
                  {strings.ticketTeamDetailAddMembers}
                </HeroButton>
              )}
            </div>

            {(teamDetail.isDefault
              ? teamDetail.members
              : teamDetail.members.filter(m => m.isActive)
            ).length === 0 ? (
              <div className={styles.emptyMessage}>{strings.ticketTeamDetailNoMembers}</div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.colName}>{strings.ticketTeamDetailColName} ▴</th>
                      <th className={styles.colEmail}>{strings.ticketTeamDetailColEmail}</th>
                      {teamDetail.isDefault && (
                        <th className={styles.colStatus}>{strings.ticketTeamDetailColStatus}</th>
                      )}
                      {!teamDetail.isDefault && (
                        <th className={styles.colRole}>{strings.ticketTeamDetailColRole}</th>
                      )}
                      <th className={styles.colAdded}>{strings.ticketTeamDetailColAdded}</th>
                      <th className={styles.colAction}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(teamDetail.isDefault
                      ? teamDetail.members
                      : teamDetail.members.filter(m => m.isActive)
                    ).map(member => (
                      <tr key={member.contactId}>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                        {teamDetail.isDefault && (
                          <td>
                            <span
                              className={cx(styles.statusBadge, {
                                [styles.statusActive]: member.isActive,
                                [styles.statusInactive]: !member.isActive,
                              })}
                            >
                              {member.isActive ? "ACTIVE" : "INACTIVE"}
                            </span>
                          </td>
                        )}
                        {!teamDetail.isDefault && (
                          <td>
                            <span className={styles.roleBadge}>{member.role}</span>
                          </td>
                        )}
                        <td>{member.addedOn}</td>
                        {canManageTeam && (
                          <td className={styles.actionCell}>
                            {/* Hide menu for managers viewing their own row on non-default teams */}
                            {(isAdmin ||
                              teamDetail.isDefault ||
                              member.contactId !== teamDetail.currentUserContactId) && (
                              <div
                                className={styles.overflowMenuContainer}
                                ref={openMenuId === member.contactId ? menuRef : null}
                              >
                                <button
                                  className={styles.overflowButton}
                                  onClick={() =>
                                    setOpenMenuId(prev =>
                                      prev === member.contactId ? null : member.contactId
                                    )
                                  }
                                  aria-label={strings.ticketTeamDetailMoreActions}
                                >
                                  ⋯
                                </button>
                                {openMenuId === member.contactId && (
                                  <div className={styles.overflowMenu} role="menu">
                                    {teamDetail.isDefault ? (
                                      <button
                                        className={styles.overflowMenuItem}
                                        role="menuitem"
                                        onClick={() => {
                                          handleToggleMemberStatus(
                                            member.contactId,
                                            !member.isActive
                                          );
                                          setOpenMenuId(null);
                                        }}
                                      >
                                        {member.isActive
                                          ? strings.ticketTeamDetailInactivate
                                          : strings.ticketTeamDetailActivate}
                                      </button>
                                    ) : (
                                      <>
                                        {isAdmin && (
                                          <button
                                            className={styles.overflowMenuItem}
                                            role="menuitem"
                                            onClick={() => {
                                              if (member.role === "TEAM MANAGER") {
                                                handleRemoveManager(member.contactId);
                                              } else {
                                                handleAssignManager(member.contactId);
                                              }
                                              setOpenMenuId(null);
                                            }}
                                          >
                                            {member.role === "TEAM MANAGER"
                                              ? strings.ticketTeamDetailRemoveManager
                                              : strings.ticketTeamDetailMakeManager}
                                          </button>
                                        )}
                                        {/* Hide Remove for managers viewing their own row */}
                                        {(isAdmin ||
                                          member.contactId !== teamDetail.currentUserContactId) && (
                                          <button
                                            className={styles.overflowMenuItem}
                                            role="menuitem"
                                            onClick={() => {
                                              handleRemoveMember(member.contactId);
                                              setOpenMenuId(null);
                                            }}
                                          >
                                            {strings.ticketTeamDetailRemove}
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {!canManageTeam && <td className={styles.actionCell} />}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Invitations Section (non-default teams only) */}
          {!teamDetail.isDefault && (
            <div className={styles.invitationsSection}>
              <div className={styles.invitationsHeader}>
                <h3 className={styles.invitationsTitle}>
                  {strings.ticketTeamDetailInvitationsTitle}
                </h3>
                {canManageTeam && (
                  <HeroButton
                    as="button"
                    className={styles.sendInviteButton}
                    onClick={() => setIsInviteModalOpen(true)}
                  >
                    {strings.ticketTeamDetailSendInvite}
                  </HeroButton>
                )}
              </div>
              {!teamDetail.invitations || teamDetail.invitations.length === 0 ? (
                <div className={styles.emptyMessage}>
                  {strings.ticketTeamDetailInviteNoInvitations}
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.colName}>
                          {strings.ticketTeamDetailInviteColLoginEmail}
                        </th>
                        <th className={styles.colEmail}>
                          {strings.ticketTeamDetailInviteColNotificationEmail}
                        </th>
                        <th className={styles.colRole}>
                          {strings.ticketTeamDetailInviteColStatus}
                        </th>
                        <th className={styles.colDate}>{strings.ticketTeamDetailInviteColSent}</th>
                        <th className={styles.colDate}>
                          {strings.ticketTeamDetailInviteColExpires}
                        </th>
                        {canManageTeam && <th className={styles.actionCell} />}
                      </tr>
                    </thead>
                    <tbody>
                      {teamDetail.invitations.map(inv => (
                        <tr key={inv.id}>
                          <td className={styles.colName}>{inv.loginEmail}</td>
                          <td className={styles.colEmail}>{inv.notificationEmail || "—"}</td>
                          <td className={styles.colRole}>
                            <span className={styles.roleBadge}>{inv.status.toUpperCase()}</span>
                          </td>
                          <td className={styles.colDate}>{inv.createdDate}</td>
                          <td className={styles.colDate}>{inv.expiresDate}</td>
                          {canManageTeam && (
                            <td className={styles.actionCell}>
                              {(inv.status === "Pending" ||
                                inv.status === "Expired" ||
                                inv.status === "Revoked") && (
                                <div
                                  className={styles.overflowMenuContainer}
                                  ref={openMenuId === `inv-${inv.id}` ? menuRef : null}
                                >
                                  <button
                                    className={styles.overflowButton}
                                    onClick={() =>
                                      setOpenMenuId(prev =>
                                        prev === `inv-${inv.id}` ? null : `inv-${inv.id}`
                                      )
                                    }
                                    aria-label={strings.ticketTeamDetailMoreActions}
                                  >
                                    ⋯
                                  </button>
                                  {openMenuId === `inv-${inv.id}` && (
                                    <div className={styles.overflowMenu} role="menu">
                                      {inv.status === "Pending" && (
                                        <button
                                          className={styles.overflowMenuItem}
                                          role="menuitem"
                                          onClick={() => {
                                            dispatch(
                                              ticketingActions.revokeInvitation.start(
                                                teamId,
                                                inv.id,
                                                accessId
                                              )
                                            );
                                            setOpenMenuId(null);
                                          }}
                                        >
                                          {strings.ticketTeamDetailInviteRemove}
                                        </button>
                                      )}
                                      {(inv.status === "Revoked" || inv.status === "Expired") && (
                                        <button
                                          className={styles.overflowMenuItem}
                                          role="menuitem"
                                          onClick={() => {
                                            dispatch(
                                              ticketingActions.renewInvitation.start(
                                                teamId,
                                                inv.id,
                                                accessId
                                              )
                                            );
                                            setOpenMenuId(null);
                                          }}
                                        >
                                          {strings.ticketTeamDetailInviteRenew}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Visibility Rule Info */}
          <div className={styles.infoCallout}>
            <strong>{strings.ticketTeamDetailVisibilityRule}</strong>{" "}
            {strings.ticketTeamDetailVisibilityDesc
              .replace(/\{0\}/g, String(teamDetail.memberCount))
              .replace(/\{1\}/g, teamDetail.teamName)}
          </div>
        </div>

        {/* Rename Modal */}
        {isRenameModalOpen && accessId && (
          <RenameTeamModal
            teamId={teamId}
            currentName={teamDetail.teamName}
            accessId={accessId}
            onClose={() => setIsRenameModalOpen(false)}
          />
        )}

        {/* Add Members Modal */}
        {isAddModalOpen && accessId && (
          <AddMembersModal
            teamId={teamId}
            accessId={accessId}
            existingContactIds={teamDetail.members.filter(m => m.isActive).map(m => m.contactId)}
            onClose={() => setIsAddModalOpen(false)}
            onSwitchToInvite={() => {
              setIsAddModalOpen(false);
              setIsInviteModalOpen(true);
            }}
          />
        )}

        {/* Invite User Modal */}
        {isInviteModalOpen && accessId && (
          <InviteUserModal
            teamId={teamId}
            accessId={accessId}
            onClose={() => setIsInviteModalOpen(false)}
          />
        )}
      </div>
    </>
  );
};

// ─── Rename Modal ──────────────────────────────────────────────────────────
type RenameTeamModalProps = {
  teamId: string;
  currentName: string;
  accessId: string;
  onClose: () => void;
};

const RenameTeamModal: React.FC<RenameTeamModalProps> = ({
  teamId,
  currentName,
  accessId,
  onClose,
}) => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const isRenaming = useSelector(getTicketingIsRenamingTeam);
  const [newName, setNewName] = useState(currentName);
  const [error, setError] = useState<string | undefined>();
  const focusTrapRef = useFocusTrap<HTMLDivElement>(onClose);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Close on success
  const prevRenaming = React.useRef(isRenaming);
  useEffect(() => {
    if (prevRenaming.current && !isRenaming) {
      onClose();
    }
    prevRenaming.current = isRenaming;
  }, [isRenaming, onClose]);

  const handleSubmit = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setError(strings.ticketCreateTeamNameRequired);
      return;
    }
    if (trimmed.length > 100) {
      setError(strings.ticketCreateTeamNameTooLong);
      return;
    }
    if (trimmed === currentName) {
      onClose();
      return;
    }
    setError(undefined);
    dispatch(ticketingActions.renameTeam.start(teamId, trimmed, accessId));
  }, [newName, currentName, dispatch, teamId, accessId, strings, onClose]);

  return (
    <div className={styles.overlay} role="presentation">
      <div
        ref={focusTrapRef}
        className={cx(styles.modal, styles.modalSmall)}
        role="dialog"
        aria-labelledby="rename-team-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="rename-team-title" className={styles.modalTitle}>
            {strings.ticketTeamDetailRenameTitle}
          </h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <label className={styles.fieldLabel}>{strings.ticketTeamDetailRenameLabel}</label>
          <input
            className={styles.nameInput}
            type="text"
            value={newName}
            maxLength={100}
            onChange={e => {
              setNewName(e.target.value);
              if (error) {
                setError(undefined);
              }
            }}
          />
          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>
        <div className={styles.modalFooter}>
          <HeroButton
            as="button"
            buttonType={ButtonType.Secondary}
            onClick={onClose}
            disabled={isRenaming}
          >
            {strings.ticketTeamDetailRenameCancel}
          </HeroButton>
          <HeroButton as="button" onClick={handleSubmit} disabled={isRenaming}>
            {isRenaming ? strings.ticketSavingLabel : strings.ticketTeamDetailRenameSave}
          </HeroButton>
        </div>
      </div>
    </div>
  );
};

// ─── Add Members Modal ─────────────────────────────────────────────────────
type AddMembersModalProps = {
  teamId: string;
  accessId: string;
  existingContactIds: string[];
  onClose: () => void;
  onSwitchToInvite: () => void;
};

const AddMembersModal: React.FC<AddMembersModalProps> = ({
  teamId,
  accessId,
  existingContactIds,
  onClose,
  onSwitchToInvite,
}) => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const members = useSelector(getTicketingDefaultTeamMembers);
  const isFetchingMembers = useSelector(getTicketingIsDefaultTeamMembersFetching);
  const isAdding = useSelector(getTicketingIsAddingMembers);
  const focusTrapRef = useFocusTrap<HTMLDivElement>(onClose);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (accessId) {
      dispatch(ticketingActions.fetchDefaultTeamMembers.start(accessId));
    }
  }, [dispatch, accessId]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Close on success
  const prevIsAdding = React.useRef(isAdding);
  useEffect(() => {
    if (prevIsAdding.current && !isAdding) {
      onClose();
    }
    prevIsAdding.current = isAdding;
  }, [isAdding, onClose]);

  // Filter out already-existing members
  const availableMembers = useMemo(() => {
    const existingSet = new Set(existingContactIds);
    return members.filter(m => !existingSet.has(m.contactId));
  }, [members, existingContactIds]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableMembers;
    }
    const q = searchQuery.toLowerCase();
    return availableMembers.filter(
      m => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    );
  }, [availableMembers, searchQuery]);

  const handleToggleMember = useCallback((contactId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredMembers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers.map(m => m.contactId)));
    }
  }, [filteredMembers, selectedIds.size]);

  const handleSubmit = useCallback(() => {
    if (selectedIds.size === 0) {
      return;
    }
    dispatch(ticketingActions.addTeamMembers.start(teamId, [...selectedIds], accessId));
  }, [selectedIds, teamId, accessId, dispatch]);

  return (
    <div className={styles.overlay} role="presentation">
      <div
        ref={focusTrapRef}
        className={styles.modal}
        role="dialog"
        aria-labelledby="add-members-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="add-members-title" className={styles.modalTitle}>
            {strings.ticketTeamDetailAddTitle}
          </h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.fieldHint}>{strings.ticketTeamDetailAddHint}</p>
          {!isFetchingMembers && availableMembers.length > 0 && (
            <input
              className={styles.searchInput}
              type="text"
              placeholder={strings.ticketTeamDetailSearchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          )}
          {!isFetchingMembers && filteredMembers.length > 0 && (
            <label className={styles.selectAllRow}>
              <input
                type="checkbox"
                checked={selectedIds.size === filteredMembers.length && filteredMembers.length > 0}
                onChange={handleSelectAll}
              />
              <span>{strings.ticketTeamDetailSelectAll}</span>
            </label>
          )}
          {isFetchingMembers ? (
            <div className={styles.memberListSpinner}>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles.memberList}>
              {filteredMembers.length === 0 ? (
                <div className={styles.emptyMessage}>
                  {searchQuery.trim() ? (
                    <>
                      <p>
                        {strings.ticketTeamDetailNoMatchPrefix.replace("{0}", searchQuery.trim())}
                      </p>
                      <p className={styles.emptyHint}>{strings.ticketTeamDetailNoMatchHint}</p>
                      <HeroButton
                        as="button"
                        style={{ marginTop: "0.75rem" }}
                        onClick={onSwitchToInvite}
                      >
                        {strings.ticketTeamDetailNoMatchSendInvite}
                      </HeroButton>
                    </>
                  ) : (
                    <p>{strings.ticketTeamDetailNoMatchHint}</p>
                  )}
                </div>
              ) : (
                filteredMembers.map(member => (
                  <MemberRow
                    key={member.contactId}
                    member={member}
                    checked={selectedIds.has(member.contactId)}
                    onToggle={handleToggleMember}
                  />
                ))
              )}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <HeroButton
            as="button"
            buttonType={ButtonType.Secondary}
            onClick={onClose}
            disabled={isAdding}
          >
            {strings.ticketTeamDetailAddCancel}
          </HeroButton>
          <HeroButton
            as="button"
            className={commonStyles.actionButton}
            onClick={handleSubmit}
            disabled={isAdding || selectedIds.size === 0}
          >
            {isAdding
              ? strings.ticketAddingLabel
              : `${strings.ticketTeamDetailAddButton} (${selectedIds.size})`}
          </HeroButton>
        </div>
      </div>
    </div>
  );
};

// ─── Member Row (for Add Modal) ────────────────────────────────────────────
type MemberRowProps = {
  member: DefaultTeamMemberData;
  checked: boolean;
  onToggle: (contactId: string) => void;
};

const MemberRow: React.FC<MemberRowProps> = ({ member, checked, onToggle }) => (
  <div
    className={styles.memberRow}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onClick={() => onToggle(member.contactId)}
    onKeyDown={e => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        onToggle(member.contactId);
      }
    }}
  >
    <input
      type="checkbox"
      className={styles.memberCheckbox}
      checked={checked}
      onChange={() => onToggle(member.contactId)}
      id={`add-member-${member.contactId}`}
      tabIndex={-1}
      onClick={e => e.stopPropagation()}
    />
    <span className={styles.memberInfo}>
      <span className={styles.memberName}>{member.name}</span>
      <span className={styles.memberEmail}>{member.email}</span>
    </span>
  </div>
);

// ─── Invite User Modal ─────────────────────────────────────────────────────
type InviteUserModalProps = {
  teamId: string;
  accessId: string;
  onClose: () => void;
};

const InviteUserModal: React.FC<InviteUserModalProps> = ({ teamId, accessId, onClose }) => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [usernameError, setUsernameError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const focusTrapRef = useFocusTrap<HTMLDivElement>(onClose);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const validateForm = (): boolean => {
    let valid = true;
    if (!username.trim()) {
      setUsernameError(strings.ticketTeamDetailInviteUsernameRequired);
      valid = false;
    } else {
      setUsernameError(undefined);
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError(strings.ticketInvalidEmailMsg);
      valid = false;
    } else {
      setEmailError(undefined);
    }
    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    dispatch(
      ticketingActions.createInvitation.start(
        teamId,
        username.trim(),
        email.trim() || undefined,
        accessId
      )
    );
    onClose();
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
    >
      <div className={cx(styles.modal, styles.modalSmall)} ref={focusTrapRef}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle} id="invite-modal-title">
            {strings.ticketTeamDetailInviteModalTitle}
          </h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.inviteInfoCallout}>
            <strong>{strings.ticketTeamDetailInviteInfoMessage.split(".")[0]}.</strong>{" "}
            {strings.ticketTeamDetailInviteInfoMessage.split(".").slice(1).join(".").trim()}
          </div>

          <div className={styles.inviteFormGroup}>
            <label className={styles.inviteLabel} htmlFor="invite-username">
              {strings.ticketTeamDetailInviteUsernameLabel}{" "}
              <span className={styles.required}>*</span>
            </label>
            <input
              id="invite-username"
              className={cx(styles.nameInput, { [styles.inputError]: usernameError })}
              type="text"
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                if (usernameError) {
                  setUsernameError(undefined);
                }
              }}
              placeholder={strings.ticketTeamDetailInviteUsernamePlaceholder}
            />
            {usernameError && <span className={styles.fieldError}>{usernameError}</span>}
            <span className={styles.inviteHint}>{strings.ticketTeamDetailInviteUsernameHint}</span>
            <div className={styles.exampleFormats}>
              <span className={styles.exampleFormatsLabel}>
                {strings.ticketTeamDetailInviteExampleFormats}
              </span>
              <span className={styles.exampleFormatChip}>wzhang@yourstudio.onmicrosoft.com</span>
            </div>
          </div>

          <div className={styles.inviteFormGroup}>
            <label className={styles.inviteLabel} htmlFor="invite-email">
              {strings.ticketTeamDetailInviteEmailLabel}
            </label>
            <input
              id="invite-email"
              className={cx(styles.nameInput, { [styles.inputError]: emailError })}
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (emailError) {
                  setEmailError(undefined);
                }
              }}
              placeholder={strings.ticketTeamDetailInviteEmailPlaceholder}
            />
            {emailError && <span className={styles.fieldError}>{emailError}</span>}
            <span className={styles.inviteHint}>{strings.ticketTeamDetailInviteEmailHint}</span>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <HeroButton
            as="button"
            buttonType={ButtonType.Secondary}
            onClick={onClose}
            disabled={isSubmitting}
          >
            {strings.ticketTeamDetailInviteCancel}
          </HeroButton>
          <HeroButton as="button" onClick={handleSubmit} disabled={isSubmitting}>
            {strings.ticketTeamDetailInviteSubmit}
          </HeroButton>
        </div>
      </div>
    </div>
  );
};

export default TeamDetailPage;
